<script lang="ts">
  import { page } from '$app/stores';
  import FlucChart from '$components/charts/FlucChart.svelte';
  import type {
    BetfairMarket,
    BookmakerMarket,
    Fluc,
    LayoutPreference,
    Runner
  } from '$types/races';
  import {
    getBookmakerMarketTypeFromLayoutPreference,
    isRaceClosed
  } from '$utils/race';
  import { currentTime } from '$utils/time';
  import type { Readable } from 'svelte/store';
  import TablerChartLine from '~icons/tabler/chart-line';

  $: ({ race } = $page.data);

  export let market: Readable<BookmakerMarket> | undefined = undefined;
  export let betfairMarket: Readable<BetfairMarket> | undefined = undefined;
  export let layoutPreference: LayoutPreference;

  $: betfairFlucType = (
    layoutPreference.side === 'BACK' ? 'backFlucs' : 'layFlucs'
  ) as 'backFlucs' | 'layFlucs';

  $: referenceTime =
    $race && isRaceClosed($race) ? $race?.startTime : $currentTime;

  // calculate the time period that we want look at
  $: timePeriod = undefined as Date | undefined;
  $: {
    // dont update anymore if race is closed
    if (layoutPreference.period === 'ALL') {
      timePeriod = undefined;
    } else if (layoutPreference.period === 'NINE_AM') {
      const nineAm = new Date(referenceTime);
      nineAm.setHours(9, 0, 0, 0);
      timePeriod = nineAm;
    } else if (layoutPreference.period === 'SIX_AM') {
      const sixAm = new Date(referenceTime);
      sixAm.setHours(6, 0, 0, 0);
      timePeriod = sixAm;
    } else if (layoutPreference.period === 'ONE_HOUR') {
      // return a date that is 1 hour ago using $currentTime
      timePeriod = new Date(referenceTime.getTime() - 60 * 60 * 1000);
    } else if (layoutPreference.period === 'TWELEVE_HOURS') {
      timePeriod = new Date(referenceTime.getTime() - 12 * 60 * 60 * 1000);
    } else if (layoutPreference.period === 'ONE_MINUTE') {
      timePeriod = new Date(referenceTime.getTime() - 60 * 1000);
    } else if (layoutPreference.period === 'FIVE_MINUTES') {
      timePeriod = new Date(referenceTime.getTime() - 5 * 60 * 1000);
    } else if (layoutPreference.period === 'TEN_MINUTES') {
      timePeriod = new Date(referenceTime.getTime() - 10 * 60 * 1000);
    } else if (layoutPreference.period === 'THIRTY_MINUTES') {
      timePeriod = new Date(referenceTime.getTime() - 30 * 60 * 1000);
    } else {
      timePeriod = undefined;
    }
  }

  $: layoutType = getBookmakerMarketTypeFromLayoutPreference(layoutPreference);

  let price = undefined as BookmakerMarket['fixedWin'] | undefined;
  let flucs: Fluc[] = [];
  $: {
    // check type of market is BookmakerMarket
    if (market && layoutType && $market && $market?.[layoutType]) {
      price = $market[layoutType];
      if (timePeriod) {
        flucs =
          price?.flucs?.filter((fluc) => {
            if (fluc.price < 1.02) {
              return false;
            } // filter out the 1.01s

            return !timePeriod || fluc.lastUpdated >= timePeriod;
          }) ?? [];
        //   always add in one more fluc before the time period
        const lastFlucIndex = price.flucs?.findIndex(
          (fluc) => fluc.lastUpdated === flucs[0]?.lastUpdated
        );
        if (
          lastFlucIndex &&
          lastFlucIndex > 0 &&
          price?.flucs?.[lastFlucIndex - 1]
        ) {
          flucs.unshift(price.flucs[lastFlucIndex - 1]);
        }
      } else {
        flucs =
          price?.flucs?.filter((fluc) => {
            if (fluc.price < 1.02) {
              return false;
            } // filter out the 1.01s
            return true;
          }) ?? [];
      }
    } else if (betfairMarket && $betfairMarket) {
      if (timePeriod) {
        flucs =
          $betfairMarket[betfairFlucType]?.filter((fluc) => {
            if (fluc.price < 1.02) {
              return false;
            } // filter out the 1.01s
            return !timePeriod || fluc.lastUpdated >= timePeriod;
          }) ?? [];
        //   always add in one more fluc before the time period
        const lastFlucIndex = $betfairMarket[betfairFlucType]?.findIndex(
          (fluc) => fluc.lastUpdated === flucs[0]?.lastUpdated
        );
        if (
          lastFlucIndex &&
          lastFlucIndex > 0 &&
          price?.flucs?.[lastFlucIndex - 1]
        ) {
          flucs.unshift(price.flucs[lastFlucIndex - 1]);
        }
      } else {
        flucs = $betfairMarket[betfairFlucType] ?? [];
      }
    }
  }

  export let height = 30;
  export let width = 90;
  export let stroke = 2;

  // calculate the % change from the opening price or first fluc until the last fluc
  $: firstNonZeroFluc =
    (price?.openingPrice ?? 0) > 1.01
      ? price?.openingPrice
      : flucs?.find((fluc) => fluc?.price > 1.01)?.price;
  let change = 0;
  $: {
    // get percentange chance of opening price
    if (!firstNonZeroFluc) {
      change = 0;
    } else {
      const lastPrice = flucs?.[flucs?.length - 1]?.price ?? 0;
      const openingPriceChance = 1 / firstNonZeroFluc;
      const lastPriceChance = 1 / lastPrice;
      const oddsChanceChange = openingPriceChance - lastPriceChance;
      change = Math.round(oddsChanceChange * 1000) / 10;
    }
  }

  $: lastPrice = flucs?.[flucs?.length - 1]?.price ?? 0;

  $: pos = () => {
    if (change > 0) return 'top';
    else return 'bottom';
  };

  let strokeColour = '';
  $: {
    if (layoutPreference.type === 'FIXED_WIN_FLUCS') {
      if (change > 0) strokeColour = '#22C55E';
      else if (change < 0) strokeColour = '#EF4444';
      else strokeColour = '#FCD34D';
    } else {
      if (change > 0) strokeColour = '#4ADE80';
      else if (change < 0) strokeColour = '#F87171';
      else strokeColour = '#4ADE80';
    }
  }

  let extraClass = '';
  $: {
    if (layoutPreference.type === 'FIXED_PLACE_FLUCS') {
      extraClass = 'bg-gray-100';
    } else {
      extraClass = '';
    }
  }
</script>

<td
  class="table-cell w-24 select-none items-center whitespace-nowrap border-l border-r bg-gradient-to-br text-center text-[13px] transition-all duration-200 ease-in-out {extraClass}"
>
  {#if flucs && flucs?.length > 1}
    <div class="relative flex justify-center">
      <FlucChart
        data={flucs}
        {width}
        {height}
        {strokeColour}
        hoverColour={strokeColour}
        strokeWidth={stroke}
        placement="bottom"
      />
      {#if lastPrice}
        <span
          class="absolute left-0 {pos()}-0 pointer-events-none rounded-md bg-white/80 px-0.5 text-[10px]"
          >{change > 0 ? '↑' : change < 0 ? '↓' : '~'}{change}%</span
        >
      {/if}
    </div>
  {:else}
    <!-- default placeholder svg -->
    <div class="relative flex justify-center text-gray-200">
      <TablerChartLine class="h-6 w-6" />
    </div>
  {/if}
</td>
